/**
 * Bottom navigation bar with 4 tabs.
 * Large touch targets, current tab highlighted, fixed to bottom.
 */

import { t } from '../../i18n';

export interface NavTab {
  id: string;
  labelKey: string;
  icon: string;
}

const TABS: NavTab[] = [
  { id: 'home',     labelKey: 'nav.home',        icon: '\uD83C\uDFE0' }, // house
  { id: 'med-list', labelKey: 'nav.medications', icon: '\uD83D\uDC8A' }, // pill
  { id: 'schedule', labelKey: 'nav.schedule',    icon: '\uD83D\uDCC5' }, // calendar
  { id: 'emr',      labelKey: 'nav.records',     icon: '\uD83D\uDCCB' }, // clipboard
  { id: 'chat',     labelKey: 'nav.ask',         icon: '\uD83D\uDCAC' }, // speech bubble
];

/**
 * Creates the bottom navigation bar element.
 * Call setActive() on the returned object to update the highlighted tab.
 */
export function createNavBar(
  onNavigate: (screen: string) => void,
): { element: HTMLElement; setActive: (id: string) => void; updateLabels: () => void } {
  const nav = document.createElement('nav');
  nav.className = 'nav-bar';
  nav.setAttribute('role', 'tablist');
  nav.setAttribute('aria-label', t('nav.mainNavigation'));

  const buttons: Map<string, HTMLButtonElement> = new Map();
  const labelSpans: Map<string, HTMLSpanElement> = new Map();

  for (const tab of TABS) {
    const btn = document.createElement('button');
    btn.className = 'nav-tab';
    btn.setAttribute('role', 'tab');
    btn.setAttribute('aria-label', t(tab.labelKey));
    btn.setAttribute('aria-selected', 'false');
    btn.setAttribute('data-screen', tab.id);
    btn.tabIndex = 0;

    // Icon
    const iconSpan = document.createElement('span');
    iconSpan.className = 'nav-tab-icon';
    iconSpan.setAttribute('aria-hidden', 'true');
    iconSpan.textContent = tab.icon;
    btn.appendChild(iconSpan);

    // Label
    const labelSpan = document.createElement('span');
    labelSpan.className = 'nav-tab-label';
    labelSpan.textContent = t(tab.labelKey);
    btn.appendChild(labelSpan);

    btn.addEventListener('click', () => {
      onNavigate(tab.id);
    });

    buttons.set(tab.id, btn);
    labelSpans.set(tab.id, labelSpan);
    nav.appendChild(btn);
  }

  // Keyboard navigation: left/right arrows move between tabs
  nav.addEventListener('keydown', (e: KeyboardEvent) => {
    const tabIds = TABS.map((tab) => tab.id);
    const focused = document.activeElement as HTMLElement;
    const currentId = focused?.getAttribute?.('data-screen');
    if (!currentId) return;

    const idx = tabIds.indexOf(currentId);
    if (idx === -1) return;

    let nextIdx = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      nextIdx = (idx + 1) % tabIds.length;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      nextIdx = (idx - 1 + tabIds.length) % tabIds.length;
    } else if (e.key === 'Home') {
      nextIdx = 0;
    } else if (e.key === 'End') {
      nextIdx = tabIds.length - 1;
    }

    if (nextIdx >= 0) {
      e.preventDefault();
      const nextBtn = buttons.get(tabIds[nextIdx]);
      if (nextBtn) {
        nextBtn.focus();
        nextBtn.click();
      }
    }
  });

  function setActive(id: string): void {
    for (const [tabId, btn] of buttons) {
      const isActive = tabId === id;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-selected', String(isActive));
      btn.tabIndex = isActive ? 0 : -1;
    }
  }

  function updateLabels(): void {
    for (const tab of TABS) {
      const span = labelSpans.get(tab.id);
      if (span) span.textContent = t(tab.labelKey);
      const btn = buttons.get(tab.id);
      if (btn) btn.setAttribute('aria-label', t(tab.labelKey));
    }
  }

  return { element: nav, setActive, updateLabels };
}
